/**
 * SnapshotPreview — an AI-produced snapshot (preview_screenshot's CAS-uploaded
 * capture, #639) rendered as a real, clickable INLINE image, not a filename
 * chip. Left-click opens the session-gated AttachmentViewer modal; right-click
 * opens a context menu with Download / Move to drive.
 *
 * The bytes are session-private (a `session_attachments` handle, no `data:`
 * URL), so the thumbnail is fetched through the owner-gated endpoint the same
 * way the viewer does — fetched fresh, object URL revoked on unmount, nothing
 * cached — so a revoked session/handle stops resolving on the next render, not
 * just on a hard refresh.
 */
import { useEffect, useState } from 'react'
import { createPortal } from 'react-dom'
import { Loader2, AlertCircle, Download, HardDriveUpload } from 'lucide-react'
import { useTheme, getThemeClass } from '@papan/core-ui/ui'
import { fetchSessionAttachment } from './sessionAttachmentsApi.js'
import { AttachmentViewer } from './AttachmentViewer.js'
import { SaveToDriveDialog } from './SaveToDriveDialog.js'

export interface SnapshotPreviewProps {
  handle: string
  mime: string
  filename: string
  sessionId?: string | null
  workspaceId?: string
}

type State =
  | { kind: 'loading' }
  | { kind: 'ready'; objectUrl: string }
  | { kind: 'error' }

/** Right-click menu for a ready preview: Download (the fetched blob) and, for a
 *  session-private handle the caller can move, Move to drive. Portalled to
 *  `document.body` so a scrolling transcript can't clip it; theme class applied
 *  by hand (it renders outside `ThemedArea`) — never on the backdrop. */
function PreviewContextMenu({
  x, y, objectUrl, filename, onDownload, onSaveToDrive, onClose,
}: {
  x: number
  y: number
  objectUrl: string
  filename: string
  onDownload: () => void
  onSaveToDrive?: () => void
  onClose: () => void
}) {
  const { settings } = useTheme()
  const themeClass = getThemeClass(settings.theme.workspace)
  // Keep the menu on-screen when the click lands near the right/bottom edge.
  const left = Math.min(x, window.innerWidth - 180)
  const top = Math.min(y, window.innerHeight - 96)

  return createPortal(
    <>
      <div
        className="fixed inset-0 z-[200]"
        onClick={onClose}
        onContextMenu={(e) => { e.preventDefault(); onClose() }}
      />
      <div
        role="menu"
        className={`${themeClass} fixed z-[201] min-w-[160px] rounded-md themed-border border themed-bg-secondary shadow-lg py-1`}
        style={{ left, top }}
      >
        <a
          role="menuitem"
          href={objectUrl}
          download={filename}
          onClick={() => { onDownload(); onClose() }}
          className="flex items-center gap-2 w-full text-left px-3 py-1.5 text-xs themed-text hover:themed-bg-tertiary transition-colors"
        >
          <Download size={12} />
          Download
        </a>
        {onSaveToDrive && (
          <button
            role="menuitem"
            onClick={() => { onSaveToDrive(); onClose() }}
            className="flex items-center gap-2 w-full text-left px-3 py-1.5 text-xs themed-text hover:themed-bg-tertiary transition-colors"
          >
            <HardDriveUpload size={12} />
            Move to drive…
          </button>
        )}
      </div>
    </>,
    document.body,
  )
}

export function SnapshotPreview({ handle, mime, filename, sessionId, workspaceId }: SnapshotPreviewProps) {
  const [state, setState] = useState<State>({ kind: 'loading' })
  const [viewerOpen, setViewerOpen] = useState(false)
  const [saveOpen, setSaveOpen] = useState(false)
  const [savedPath, setSavedPath] = useState<string | null>(null)
  const [menuPos, setMenuPos] = useState<{ x: number; y: number } | null>(null)

  useEffect(() => {
    if (!sessionId || !workspaceId) { setState({ kind: 'error' }); return }
    let cancelled = false
    let objectUrl: string | null = null
    setState({ kind: 'loading' })

    fetchSessionAttachment(sessionId, workspaceId, handle)
      .then(async (res) => {
        if (!res.ok) throw new Error(`status ${res.status}`)
        const blob = await res.blob()
        if (cancelled) return
        objectUrl = URL.createObjectURL(blob)
        setState({ kind: 'ready', objectUrl })
      })
      .catch(() => { if (!cancelled) setState({ kind: 'error' }) })

    return () => {
      cancelled = true
      if (objectUrl) URL.revokeObjectURL(objectUrl)
    }
  }, [handle, sessionId, workspaceId])

  const canSave = !!sessionId && !!workspaceId

  if (state.kind === 'error') {
    return (
      <div className="inline-flex items-center gap-1.5 text-xs themed-text-muted rounded-md themed-border border px-2 py-1">
        <AlertCircle size={12} />
        Snapshot unavailable
      </div>
    )
  }

  return (
    <>
      {state.kind === 'loading' ? (
        <div className="w-44 h-28 rounded-md themed-border border themed-bg-tertiary flex items-center justify-center animate-pulse">
          <Loader2 size={16} className="animate-spin themed-text-muted" />
        </div>
      ) : (
        <button
          type="button"
          onClick={() => setViewerOpen(true)}
          onContextMenu={(e) => { e.preventDefault(); setMenuPos({ x: e.clientX, y: e.clientY }) }}
          title={`Open ${filename}`}
          className="block rounded-md overflow-hidden themed-border border hover:opacity-90 transition-opacity cursor-pointer"
        >
          <img
            src={state.objectUrl}
            alt={filename}
            className="max-w-full max-h-64 object-contain"
          />
        </button>
      )}
      {savedPath && (
        <div className="text-[10px] themed-text-muted mt-0.5">Saved to {savedPath}</div>
      )}
      {menuPos && state.kind === 'ready' && (
        <PreviewContextMenu
          x={menuPos.x}
          y={menuPos.y}
          objectUrl={state.objectUrl}
          filename={filename}
          onDownload={() => { /* the <a download> does the save */ }}
          onSaveToDrive={canSave ? () => setSaveOpen(true) : undefined}
          onClose={() => setMenuPos(null)}
        />
      )}
      {viewerOpen && (
        <AttachmentViewer
          attachment={{ filename, mime, handle }}
          sessionId={sessionId ?? ''}
          workspaceId={workspaceId ?? ''}
          onClose={() => setViewerOpen(false)}
          onSaveToDrive={canSave ? () => { setViewerOpen(false); setSaveOpen(true) } : undefined}
        />
      )}
      {saveOpen && canSave && (
        <SaveToDriveDialog
          filename={filename}
          handle={handle}
          sessionId={sessionId!}
          workspaceId={workspaceId!}
          onClose={() => setSaveOpen(false)}
          onSaved={(result) => { setSaveOpen(false); setSavedPath(result.path) }}
        />
      )}
    </>
  )
}
